import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiShield,
  FiLock,
  FiMapPin,
  FiCamera,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiSearch,
  FiSmartphone,
  FiDatabase,
  FiUsers,
  FiFileText,
  FiHeadphones,
  FiEye,
  FiAlertCircle,
  FiServer
} from 'react-icons/fi';

const HIGHLIGHTS = [
  {
    icon: FiShield,
    color: 'bg-emerald-100 text-emerald-600',
    title: 'عدم بيع البيانات',
    desc: 'لا نبيع ولا نؤجر بياناتك الشخصية لأي طرف ثالث لأغراض تسويقية مطلقاً.'
  },
  {
    icon: FiMapPin,
    color: 'bg-blue-100 text-blue-600',
    title: 'الموقع الجغرافي',
    desc: 'نستخدم إذن الموقع فقط لربط العميل بالمزود القريب وعرض مكان تنفيذ الخدمة.'
  },
  {
    icon: FiLock,
    color: 'bg-purple-100 text-purple-600',
    title: 'تشفير وأمان',
    desc: 'بياناتك مشفرة بالكامل أثناء النقل (HTTPS/SSL) وتُحفظ في خوادم سحابية آمنة.'
  },
  {
    icon: FiTrash2,
    color: 'bg-rose-100 text-rose-600',
    title: 'حذف الحساب والبيانات',
    desc: 'يمكنك طلب حذف حسابك وكافة بياناتك المرتبطة به بضغطة زر في أي وقت.'
  }
];

const SECTIONS = [
  {
    id: 'intro',
    icon: FiShield,
    title: '1. مقدمة ونطاق سياسة الخصوصية',
    tldr: 'توضح هذه السياسة كيفية جمع بياناتك واستخدامها وحمايتها في تطبيق خدمة وفق متطلبات متجر غوغل بلاي.',
    body: [
      'نحن في تطبيق "خدمة" (Khidma App) نولي خصوصية مستخدمينا (سواء كانوا عملاء أو مقدمي خدمات) أهمية بالغة ونلتزم بحماية بياناتهم الشخصية وتوفير بيئة استخدام آمنة وشفافة.',
      'تُمثّل سياسة الخصوصية هذه وثيقة رسمية تشرح بوضوح البيانات التي نجمعها، والأغراض التي نستخدمها لأجلها، والحقوق المتاحة لك للتحكم ببياناتك.',
      'تتوافق هذه السياسة بشكل كامل مع سياسات مطوري غوغل بلاي (Google Play Developer Policies) وقوانين حماية البيانات المعمول بها.'
    ]
  },
  {
    id: 'data-collected',
    icon: FiDatabase,
    title: '2. البيانات التي نجمعها (Data We Collect)',
    tldr: 'نجمع البيانات الضرورية فقط لتشغيل الخدمة وتسهيل التواصل والمعاملات المالية.',
    subsections: [
      {
        subtitle: 'أ. البيانات الشخصية والمُعرِّفة:',
        text: 'تشمل الاسم الكامل، رقم الهاتف، البريد الإلكتروني، وصورة الملف الشخصي التي تزودنا بها عند إنشاء الحساب.'
      },
      {
        subtitle: 'ب. بيانات الموقع الجغرافي (Location Data):',
        text: 'نجمع بيانات الموقع الجغرافي الدقيق أو التقريبي فقط عندما تمنحنا الإذن بذلك، لاستخدامها في ربط طلبات الخدمات بين العملاء ومقدمي الخدمات القريبين وإظهار المسافات.'
      },
      {
        subtitle: 'جـ. البيانات المالية والمعاملات:',
        text: 'تتضمن سجلات المحفظة الرقمية، إيصالات التحويل البنكي، تفاصيل الشراء من المتجر الرقمي، ورقم الحساب البنكي (RIB) الخاص بمقدمي الخدمة لأغراض سحب المستحقات.'
      },
      {
        subtitle: 'د. الصور والملفات المرفوقة:',
        text: 'الصور والمستندات التي تقوم برفعها مثل (صورة الشيك المشطوب لتأكيد الحساب البنكي، صور نماذج الأعمال، وإيصالات الدفع).'
      },
      {
        subtitle: 'هـ. البيانات التقنية ومعرفات الجهاز:',
        text: 'عنوان IP، نوع الجهاز، نظام التشغيل، المعرف الفريد للجهاز (Device Identifier)، وبيانات السجلات التقنية لضمان استقرار التطبيق وإرسال الإشعارات.'
      }
    ]
  },
  {
    id: 'data-usage',
    icon: FiFileText,
    title: '3. كيف نستخدم بياناتك (How We Use Your Data)',
    tldr: 'نستخدم البيانات لتنفيذ الطلبات، معالجة المدفوعات، وتحسين جودة وأمان التطبيق.',
    body: [
      'تسهيل وتنسيق طلبات الخدمات بين العملاء ومقدمي الخدمات وإتاحة التواصل المباشر.',
      'إدارة وتنفيذ المعاملات المالية، رصيد المحفظة، واشتراكات المتجر الرقمي بدقة وأمان.',
      'إرسال إشعارات التحديثات الفورية (Push Notifications) المتعلقة بحالة الطلبات، المحادثات، والتنبيهات الهامة.',
      'التحقق من هوية المستخدمين لمنع الاحتيال والانتحال وضمان سلامة المنصة.',
      'تقديم الدعم الفني والاستجابة لاستفساراتك وشكاواك بسرعة وفعالية.',
      'تحسين وتطوير ميزات التطبيق واستكشاف الأخطاء البرمجية وإصلاحها.'
    ]
  },
  {
    id: 'permissions',
    icon: FiSmartphone,
    title: '4. أذونات الجهاز المطلوبة (App Permissions)',
    tldr: 'جميع الأذونات اختيارية ولا تُطلب إلا عند استخدام الميزة المتعلقة بها.',
    subsections: [
      {
        subtitle: 'إذن الموقع (ACCESS_FINE_LOCATION / ACCESS_COARSE_LOCATION):',
        text: 'يُستخدم لتحديد موقع الخدمة المطلوبة وعرض الخدمات القريبة منك على الخريطة.'
      },
      {
        subtitle: 'إذن الكاميرا والمعرض (CAMERA & Storage Permissions):',
        text: 'يُستخدم للسماح لك بالتقاط أو اختيار صور للملف الشخصي، إيصالات الدفع، أو نماذج الأعمال.'
      },
      {
        subtitle: 'إذن الإشعارات (POST_NOTIFICATIONS):',
        text: 'يُستخدم لإرسال تنبيهات لحظية عند تلقي عرض جديد، تحديث حالة الطلب، أو رسالة محادثة جديدة.'
      }
    ]
  },
  {
    id: 'third-parties',
    icon: FiServer,
    title: '5. مشاركة البيانات مع الأطراف الثالثة (Third-Party Services)',
    tldr: 'نشارك البيانات فقط مع خدمات سحابية موثوقة ومبسطة بالقدر اللازم لتقديم الخدمة.',
    body: [
      'المشاركة بين المستخدمين: عند الاتفاق على خدمة، يتم مشاركة الاسم، رقم الهاتف، والموقع المحدد فقط بين العميل ومقدم الخدمة المعني للتنفيذ.',
      'مزود قواعد البيانات والمصادقة (Supabase): تُخزن البيانات وحسابات المستخدمين في قواعد بيانات سحابية مشفرة ومؤمنة للغاية لدى Supabase.',
      'مزود الإشعارات (Firebase Cloud Messaging): نستخدم خدمات غوغل لإرسال التنبيهات الفورية المشفرة إلى جهازك.',
      'حماية الشبكة (Cloudflare): لتأمين حركة المرور وحماية التطبيق من الهجمات الإلكترونية.',
      'الامتثال القانوني: قد نفصح عن معلومات شخصية إذا طُلبت منا بموجب القانون أو بأمر قضائي رسمي نافذ.'
    ]
  },
  {
    id: 'security',
    icon: FiLock,
    title: '6. أمان وتشفير البيانات (Data Security & Protection)',
    tldr: 'نطبق أعلى معايير التشفير (SSL/TLS) لحماية بياناتك من الوصول غير المصرح به.',
    body: [
      'تُنقل جميع البيانات بين التطبيق والخوادم عبر اتصالات تشفير آمنة ومُعتمَدة (HTTPS / SSL).',
      'تخضع قواعد البيانات لقيود وصول صارمة مع تشفير البيانات الحساسة أثناء التخزين.',
      'نحن لا نخزن بيانات بطاقات الدفع البنكية الحساسة على خوادمنا بل نستخدم بوابات دفع وسيطة معتمدة.',
      'نلزم جميع الموظفين والشركاء بتعليمات صارمة للحفاظ على سرية وحماية بيانات المستخدمين.'
    ]
  },
  {
    id: 'deletion',
    icon: FiTrash2,
    title: '7. سياسة حذف الحساب والبيانات (Account & Data Deletion)',
    tldr: 'استيفاءً لمتطلبات Google Play، يمكنك طلب حذف كافة بياناتك بضغطة زر مجاناً.',
    body: [
      'التزاماً بسياسات غوغل بلاي الشفافة، يوفر تطبيق "خدمة" خياراً سهلاً ومباشراً لحذف الحساب والبيانات من داخل التطبيق ومن خلال موقع الويب.',
      'يمكنك الانتقال إلى صفحة طلب حذف الحساب عبر الرابط المباشر (/delete-account) وتأكيد الطلب.',
      'عند تأكيد الحذف، يتم إزالة كافة بياناتك الشخصية (الاسم، الهاتف، البريد، الصور، المحفظة) نهائياً من قواعد بياناتنا خلال 7 أيام عمل كحد أقصى.'
    ]
  },
  {
    id: 'children',
    icon: FiUsers,
    title: '8. خصوصية الأطفال (Children Privacy)',
    tldr: 'التطبيق غير موجه للأطفال دون سن 13 عاماً ولا نجمع بياناتهم عَمداً.',
    body: [
      'تطبيق "خدمة" موجّه للاستخدام من قِبل البالغين والأشخاص القادرين على إجراء المعاملات وتسيير الخدمات.',
      'لا نجمع أو نطلب عَمداً أي بيانات شخصية من أطفال تقل أعمارهم عن 13 عاماً.',
      'إذا تبين لنا جمع بيانات طفل دون إذن ولي الأمر، سنقوم بحذف تلك البيانات فوراً من سلك قواعد بياناتنا.'
    ]
  },
  {
    id: 'changes',
    icon: FiAlertCircle,
    title: '9. التعديلات على سياسة الخصوصية (Policy Changes)',
    tldr: 'قد نحدث هذه السياسة لمواكبة التطورات وتصلك إشعارات بالتعديلات الجوهرية.',
    body: [
      'نحتفظ بالحق في تحديث سياسة الخصوصية هذه من وقت لآخر لمواكبة التطورات التقنية أو القانونية.',
      'عند إجراء تغييرات جوهرية، سنقوم بإعلامك عبر إشعار داخل التطبيق أو البريد الإلكتروني مع تحديث تاريخ التعديل في أعلى الصفحة.'
    ]
  },
  {
    id: 'contact',
    icon: FiHeadphones,
    title: '10. التواصل واستفسارات الخصوصية (Contact Us)',
    tldr: 'فريق مسؤول حماية البيانات جاهز للإجابة على جميع استفساراتك الخصوصية.',
    body: [
      'إذا كان لديك أي سؤال، استفسار، أو طلب متعلق بسياسة الخصوصية وحماية بياناتك، يمكنك التواصل معنا مباشرة عبر صفحة التواصل أو البريد الإلكتروني الخاص بالدعم.'
    ]
  }
];

const PrivacyPolicyScreen = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [searchQuery, setSearchQuery] = useState('');

  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;

  const filteredSections = SECTIONS.filter(
    (sec) =>
      sec.title.includes(searchQuery) ||
      sec.tldr.includes(searchQuery) ||
      (sec.body && sec.body.some((b) => b.includes(searchQuery))) ||
      (sec.subsections && sec.subsections.some((sub) => sub.subtitle.includes(searchQuery) || sub.text.includes(searchQuery)))
  );

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F8F9FA] text-gray-800" style={{ fontFamily: "'Tajawal', sans-serif" }}>
      {/* Top Header Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-black font-semibold text-sm transition-colors py-1 px-3 rounded-lg hover:bg-gray-100"
          >
            <FiChevronRight className="w-5 h-5" />
            <span>العودة</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-sm">
              <FiCheckCircle className="w-4 h-4 text-emerald-600" />
              <span>متوافق مع سياسات Google Play</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Header Section */}
      <section className="bg-gradient-to-b from-white to-[#F8F9FA] border-b border-gray-200 py-10 md:py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center p-3.5 bg-emerald-500/10 text-emerald-600 rounded-2xl mb-4">
            <FiShield className="w-9 h-9" />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight leading-tight">
            سياسة الخصوصية وحماية البيانات
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6">
            شفافية كاملة حول كيفية جمع بياناتك، استخدامها، وحمايتها في تطبيق "خدمة". نحن نلتزم بأعلى المعايير لحماية خصوصيتك.
          </p>

          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm px-4 py-1.5 rounded-full text-xs text-gray-500 font-medium">
            <span>تاريخ أحدث تحديث:</span>
            <span className="font-bold text-gray-800">{formattedDate}</span>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Quick Highlights Cards */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FiEye className="text-emerald-600 w-5 h-5" />
            <span>ملخص السياسة باختصار (Highlights)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${h.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">{h.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{h.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8 max-w-xl mx-auto">
          <div className="relative">
            <FiSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث في بنود سياسة الخصوصية..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pr-12 pl-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent shadow-sm transition"
            />
          </div>
        </div>

        {/* Layout: Sticky Sidebar + Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Quick Index Sidebar (Desktop) */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-20 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm max-h-[80vh] overflow-y-auto">
            <h3 className="font-bold text-gray-900 text-sm mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
              <FiFileText className="text-emerald-600" />
              <span>فهرس بنود السياسة</span>
            </h3>
            <nav className="space-y-1">
              {SECTIONS.map((sec) => {
                const Icon = sec.icon;
                const isActive = activeSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`w-full flex items-center justify-between text-right px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <span className="truncate">{sec.title}</span>
                    </div>
                    <FiChevronLeft className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'translate-x-0 text-emerald-600' : 'text-gray-300'}`} />
                  </button>
                );
              })}
            </nav>

            <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
              <Link
                to="/rules-and-terms"
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-xs font-bold text-gray-700 transition"
              >
                <span>القواعد والشروط</span>
                <FiChevronLeft />
              </Link>
              <Link
                to="/delete-account"
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-xs font-bold text-red-700 transition"
              >
                <span>حذف الحساب والبيانات</span>
                <FiChevronLeft />
              </Link>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="lg:col-span-8 space-y-6">
            {filteredSections.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-200 shadow-sm">
                <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-700 font-bold mb-1">لم نجد أي بنود تطابق بحثك</p>
                <p className="text-xs text-gray-500">جرب البحث بكلمات أخرى مثل "الموقع"، "الحساب"، أو "الأمان".</p>
              </div>
            ) : (
              filteredSections.map((sec) => {
                const Icon = sec.icon;
                return (
                  <article
                    key={sec.id}
                    id={sec.id}
                    className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200/90 shadow-sm scroll-mt-24 transition hover:border-gray-300"
                  >
                    {/* Header of section */}
                    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-gray-100">
                      <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl shrink-0 mt-0.5">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 leading-snug">{sec.title}</h2>
                        {sec.tldr && (
                          <div className="inline-block bg-emerald-500/10 text-emerald-800 text-xs font-bold px-3 py-1 rounded-lg mt-1">
                            💡 ملخص: {sec.tldr}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section Body */}
                    {sec.body && (
                      <div className="space-y-3 text-sm md:text-base text-gray-700 leading-relaxed">
                        {sec.body.map((para, i) => (
                          <p key={i} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 shrink-0" />
                            <span>{para}</span>
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Subsections if any */}
                    {sec.subsections && (
                      <div className="space-y-4 mt-3">
                        {sec.subsections.map((sub, idx) => (
                          <div key={idx} className="bg-gray-50/80 rounded-xl p-4 border border-gray-100">
                            <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1 text-emerald-700">
                              {sub.subtitle}
                            </h4>
                            <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{sub.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })
            )}

            {/* Bottom Cards: Contact & Delete Account & Terms */}
            <div className="pt-6 space-y-4">
              {/* Google Play Deletion Card */}
              <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-2xl p-6 md:p-8 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-md mb-2">
                    <FiTrash2 className="w-3.5 h-3.5" />
                    <span>متطلب غوغل بلاي الرسمي</span>
                  </div>
                  <h3 className="font-bold text-xl mb-1">تريد حذف حسابك ورسم كافة بياناتك؟</h3>
                  <p className="text-white/90 text-xs md:text-sm max-w-xl leading-relaxed">
                    نوفر لك آلية سريعة ومباشرة للتقدم بطلب حذف الحساب وجميع البيانات المرتبطة به نهائياً وبدون أي تعقيد.
                  </p>
                </div>
                <Link
                  to="/delete-account"
                  className="shrink-0 bg-white text-red-700 font-bold px-6 py-3 rounded-xl text-sm hover:bg-gray-100 transition-colors shadow-sm flex items-center gap-2"
                >
                  <span>طلب حذف الحساب</span>
                  <FiChevronLeft />
                </Link>
              </div>

              {/* Contact Support & Terms grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                      <FiHeadphones className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">استفسار حول الخصوصية؟</h4>
                    <p className="text-xs text-gray-600 leading-relaxed mb-4">
                      فريق الدعم وحماية البيانات جاهز للإجابة على جميع تساؤلاتك.
                    </p>
                  </div>
                  <Link
                    to="/contact-us"
                    className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-gray-800 transition"
                  >
                    <span>التواصل معنا</span>
                    <FiChevronLeft />
                  </Link>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center mb-3">
                      <FiFileText className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">القواعد والشروط العامة</h4>
                    <p className="text-xs text-gray-600 leading-relaxed mb-4">
                      اطلع على شروط استخدام منصة خدمة والحقوق والتزامات الأطراف.
                    </p>
                  </div>
                  <Link
                    to="/rules-and-terms"
                    className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-800 text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-gray-200 transition"
                  >
                    <span>عرض القواعد والشروط</span>
                    <FiChevronLeft />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 text-center text-xs text-gray-500">
        <p>جميع الحقوق محفوظة © {new Date().getFullYear()} تطبيق خدمة (Khidma App)</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicyScreen;
